import React, { useState } from "react";
import { Users, Check, Plus, ShieldCheck, User as UserIcon } from "lucide-react";
import { useLang } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import { useCatalog } from "../../context/CatalogContext";
import { Button } from "../../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";

export default function AdminUsers() {
  const { t } = useLang();
  const { users, grantAccess, revokeAccess } = useAuth();
  const { adminBooks: books, getBook } = useCatalog();
  const [managing, setManaging] = useState(null); // user

  const mUser = users.find((u) => u.id === managing);

  return (
    <div className="animate-float-up">
      <h1 className="font-display text-3xl font-semibold tracking-tight mb-1">{t("admin_users")}</h1>
      <p className="text-sm text-muted-foreground mb-8">
        {"Manage users and their entitlements"}
      </p>

      <div className="rounded-2xl border border-border bg-card/50 overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="border-b border-border text-left text-xs text-muted-foreground uppercase tracking-wide">
              <th className="px-4 py-3 font-medium">{t("full_name")}</th>
              <th className="px-4 py-3 font-medium">{t("email")}</th>
              <th className="px-4 py-3 font-medium">Role</th>
              <th className="px-4 py-3 font-medium">{t("admin_entitlements")}</th>
              <th className="px-4 py-3 font-medium text-right">{t("actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {users.map((u) => (
              <tr key={u.id} className="hover:bg-secondary/40 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-brand-950 text-sm font-bold">
                      {u.name.charAt(0).toUpperCase()}
                    </span>
                    <span className="font-medium">{u.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                <td className="px-4 py-3">
                  <Badge variant="outline" className={u.role === "admin" ? "bg-brand-500/15 text-brand-400 border-brand-500/30" : "bg-secondary text-muted-foreground border-border"}>
                    {u.role === "admin" ? <ShieldCheck className="w-3 h-3 mr-1" /> : <UserIcon className="w-3 h-3 mr-1" />}
                    {u.role}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{u.library.length} {"audiobooks"}</td>
                <td className="px-4 py-3 text-right">
                  <Button variant="outline" size="sm" className="rounded-full border-border" onClick={() => setManaging(u.id)}>
                    {t("grant_access")}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!managing} onOpenChange={(o) => !o && setManaging(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {t("admin_entitlements")}
              {mUser && <span className="text-muted-foreground text-base font-normal"> · {mUser.name}</span>}
            </DialogTitle>
          </DialogHeader>
          {mUser && (
            <div className="space-y-2 py-2">
              {books.map((b) => {
                const owned = mUser.library.includes(b.id);
                return (
                  <div key={b.id} className="flex items-center gap-3 p-2.5 rounded-xl border border-border bg-card/50">
                    <img src={b.cover} alt="" className="w-9 h-13 h-[52px] rounded object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{b.title_en}</p>
                      <p className="text-xs text-muted-foreground truncate">{b.author}</p>
                    </div>
                    {owned ? (
                      <Button size="sm" variant="outline" className="rounded-full border-emerald-500/30 text-emerald-400" onClick={() => revokeAccess(mUser.id, b.id)}>
                        <Check className="w-3.5 h-3.5 mr-1" /> {t("revoke")}
                      </Button>
                    ) : (
                      <Button size="sm" className="rounded-full bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold" onClick={() => grantAccess(mUser.id, b.id)}>
                        <Plus className="w-3.5 h-3.5 mr-1" /> {t("grant_access")}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
